import { useEffect, useState } from "react";
import { X, Gift, Check, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "lx:welcome-popup-v1";
const TRIGGER_DELAY_MS = 25 * 1000; // 25 s after first paint
const DISMISS_DAYS = 30;

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const writeState = (s) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
};

/** Shows a 5%-off welcome popup ~25 s into a visitor's first session.
 *  Dismissed for 30 days; permanently dismissed once they redeem.
 *  Skipped for logged-in users (we already have their email). */
const WelcomePopup = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) return; // skip for logged-in customers
    const state = readState();
    if (state?.redeemed) return; // permanent dismiss
    if (state?.dismissed_until && state.dismissed_until > Date.now()) return;

    const t = setTimeout(() => setOpen(true), TRIGGER_DELAY_MS);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => {
    setOpen(false);
    writeState({
      ...(readState() || {}),
      dismissed_until: Date.now() + DISMISS_DAYS * 24 * 3600 * 1000,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setErr("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const d = await api.welcomeCoupon(email.trim().toLowerCase());
      setCoupon(d);
      writeState({ redeemed: true, email: email.trim().toLowerCase(), code: d.code });
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async () => {
    if (!coupon?.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-end md:items-center justify-center p-4 fade-in"
      onClick={close}
      data-testid="welcome-popup-overlay"
    >
      <div
        className="bg-white max-w-md w-full p-7 md:p-9 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-popup"
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 text-neutral-500 hover:text-black"
          data-testid="welcome-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {!coupon ? (
          <>
            <Gift className="w-7 h-7 text-amber-500 mb-3" />
            <p className="overline text-neutral-500 mb-2">Welcome</p>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              5% off your first&nbsp;piece
            </h2>
            <p className="text-sm text-neutral-600 mt-3">
              Join the atelier mailing list — early access to drops, restocks and a one-time
              welcome credit you can apply at checkout.
            </p>
            <form onSubmit={submit} className="mt-6" data-testid="welcome-popup-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full border-b border-black/15 focus:border-black bg-transparent py-2 text-sm outline-none"
                data-testid="welcome-popup-email"
              />
              {err && <p className="text-xs text-red-600 mt-2" data-testid="welcome-popup-error">{err}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="btn-ink w-full mt-5 disabled:opacity-50"
                data-testid="welcome-popup-submit"
              >
                {submitting ? "Issuing your code…" : "Send my 5% code"}
              </button>
              <button
                type="button"
                onClick={close}
                className="text-xs text-neutral-500 underline-offset-4 hover:underline w-full mt-3"
                data-testid="welcome-popup-skip"
              >
                No thanks
              </button>
            </form>
          </>
        ) : (
          <div data-testid="welcome-popup-success">
            <Check className="w-7 h-7 text-emerald-600 mb-3" />
            <p className="overline text-neutral-500 mb-2">Your code is ready</p>
            <h2 className="font-serif-display text-3xl md:text-4xl">
              Welcome to Luxsova
            </h2>
            <p className="text-sm text-neutral-600 mt-3">
              Apply this code at checkout for 5% off your first piece. It's valid for 30 days.
            </p>
            <div className="mt-6 flex items-stretch border border-black/15">
              <code
                className="flex-1 px-4 py-3 font-mono tracking-widest text-lg text-center select-all"
                data-testid="welcome-popup-code"
              >
                {coupon.code}
              </code>
              <button
                onClick={copyCode}
                className="px-4 border-l border-black/15 hover:bg-neutral-50 inline-flex items-center gap-1.5 text-xs"
                data-testid="welcome-popup-copy"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button onClick={close} className="btn-outline w-full mt-6" data-testid="welcome-popup-done">
              Continue browsing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePopup;
